import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

// Top chrome stays visible until the pointer leaves its zone.
const TOP_HIDE_DELAY_MS = 300;
// A frame entering Focus shows its controls once so people learn where they are.
const FOCUS_ENTRY_REVEAL_MS = 3000;
// The bottom composer gets a little longer — it may hold a half-typed reply.
const BOTTOM_AUTO_HIDE_MS = 3400;

function topInteracting(root: HTMLElement | null): boolean {
  if (!root) return false;
  const actions = root.querySelector('[data-frame-hover-actions="true"]');
  if (!actions) return false;
  const hoverTarget =
    root.dataset.frameFocus === "true"
      ? actions.querySelector('[data-frame-hover-right-target="true"]')
      : actions;
  if (hoverTarget?.matches(":hover")) return true;
  return actions.contains(document.activeElement);
}

function topKeyboardInteracting(root: HTMLElement | null): boolean {
  const actions = root?.querySelector('[data-frame-hover-actions="true"]');
  return (
    !!actions?.contains(document.activeElement) ||
    (document.activeElement instanceof Element &&
      document.activeElement.matches('[data-frame-chrome-drag-strip="true"]'))
  );
}

function focusRightHovering(
  root: HTMLElement,
  x: number,
  y: number,
  maximized: boolean,
): boolean {
  const rect = root.getBoundingClientRect();
  const buttons = root.querySelector('[data-frame-hover-right-target="true"]');
  const buttonWidth = buttons?.getBoundingClientRect().width ?? 0;
  return (
    x >= rect.right - Math.max(96, buttonWidth + 8) &&
    x <= rect.right + (maximized ? 0 : 8) &&
    y >= rect.top - (maximized ? 0 : 40) &&
    y <= rect.top + (maximized ? 44 : 16)
  );
}

function bottomInteracting(root: HTMLElement | null): boolean {
  if (!root) return false;
  const composer = root.querySelector('[data-frame-hover-composer="true"]');
  if (!composer) return false;
  if (composer.matches(":hover")) return true;
  if (composer.contains(document.activeElement)) return true;
  const field = composer.querySelector("textarea, input");
  if (
    field instanceof HTMLTextAreaElement ||
    field instanceof HTMLInputElement
  ) {
    return field.value.trim().length > 0;
  }
  return false;
}

export function useFrameHoverActivation(
  rootRef: RefObject<HTMLElement | null>,
  focused = false,
  maximized = false,
) {
  const bottomActiveRef = useRef(false);
  const topActiveRef = useRef(false);
  const [bottomActive, setBottomActive] = useState(false);
  const [topActive, setTopActive] = useState(false);
  const [entryRevealActive, setEntryRevealActive] = useState(false);
  const entryRevealShownRef = useRef(false);
  const entryRevealTimerRef = useRef<number | null>(null);
  const rightZoneHoverRef = useRef(false);
  const hoverArmedRef = useRef(false);
  // Snoozed edges stay hidden while the cursor remains inside the frame,
  // even across the hover zone. Leaving the frame re-arms both edges.
  const topSnoozedRef = useRef(false);
  const bottomSnoozedRef = useRef(false);
  const topTimerRef = useRef<number | null>(null);
  const bottomTimerRef = useRef<number | null>(null);

  const clearTimer = useCallback((timerRef: RefObject<number | null>) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const snoozeTop = useCallback(() => {
    topSnoozedRef.current = true;
    clearTimer(topTimerRef);
    if (topActiveRef.current) {
      topActiveRef.current = false;
      setTopActive(false);
    }
  }, [clearTimer]);

  const snoozeBottom = useCallback(() => {
    bottomSnoozedRef.current = true;
    clearTimer(bottomTimerRef);
    if (bottomActiveRef.current) {
      bottomActiveRef.current = false;
      setBottomActive(false);
    }
  }, [clearTimer]);

  const scheduleAutoHide = useCallback(
    (edge: "top" | "bottom") => {
      const timerRef = edge === "top" ? topTimerRef : bottomTimerRef;
      const delay = edge === "top" ? TOP_HIDE_DELAY_MS : BOTTOM_AUTO_HIDE_MS;
      clearTimer(timerRef);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const interacting =
          edge === "top"
            ? topInteracting(rootRef.current)
            : bottomInteracting(rootRef.current);
        if (interacting) {
          // Still in use (reaching for a button, typing a reply) — check
          // again later instead of yanking it away.
          scheduleAutoHide(edge);
          return;
        }
        if (edge === "top") snoozeTop();
        else snoozeBottom();
      }, delay);
    },
    [clearTimer, rootRef, snoozeBottom, snoozeTop],
  );

  const updateBottomActive = useCallback(
    (active: boolean) => {
      if (!active) {
        clearTimer(bottomTimerRef);
        if (!bottomActiveRef.current) return;
        bottomActiveRef.current = false;
        setBottomActive(false);
        return;
      }
      // Snoozed: stay hidden until the cursor leaves the frame and returns.
      if (bottomSnoozedRef.current) return;
      if (bottomActiveRef.current) return;
      bottomActiveRef.current = true;
      setBottomActive(true);
      scheduleAutoHide("bottom");
    },
    [clearTimer, scheduleAutoHide],
  );

  const updateTopActive = useCallback(
    (active: boolean) => {
      if (!active && (topTimerRef.current !== null || !topActiveRef.current))
        return;
      clearTimer(topTimerRef);
      if (active) {
        topActiveRef.current = true;
        setTopActive(true);
      } else {
        topTimerRef.current = window.setTimeout(() => {
          topTimerRef.current = null;
          if (topInteracting(rootRef.current)) return;
          topActiveRef.current = false;
          setTopActive(false);
          topTimerRef.current = null;
        }, TOP_HIDE_DELAY_MS);
      }
    },
    [clearTimer, rootRef],
  );

  const beginEntryReveal = useCallback(() => {
    if (entryRevealShownRef.current) return;
    entryRevealShownRef.current = true;
    hoverArmedRef.current = false;
    clearTimer(topTimerRef);
    topActiveRef.current = false;
    setTopActive(false);
    setEntryRevealActive(true);
    entryRevealTimerRef.current = window.setTimeout(() => {
      entryRevealTimerRef.current = null;
      hoverArmedRef.current = !rightZoneHoverRef.current;
      setEntryRevealActive(false);
      if (!topKeyboardInteracting(rootRef.current)) {
        topActiveRef.current = false;
        setTopActive(false);
      }
    }, FOCUS_ENTRY_REVEAL_MS);
  }, [clearTimer, rootRef]);

  const updateFocusHover = useCallback(
    (root: HTMLElement, x: number, y: number) => {
      const inRightZone = focusRightHovering(root, x, y, maximized);
      rightZoneHoverRef.current = inRightZone;
      if (entryRevealTimerRef.current !== null) return;
      if (maximized) {
        updateTopActive(inRightZone);
        return;
      }
      if (!entryRevealShownRef.current) return;
      if (!inRightZone) hoverArmedRef.current = true;
      updateTopActive(inRightZone && hoverArmedRef.current);
    },
    [maximized, updateTopActive],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const revealDepth = Math.min(88, rect.height * 0.25);
      const composerFocused = rootRef.current
        ?.querySelector('[data-frame-hover-composer="true"]')
        ?.contains(document.activeElement);
      const nearBottom = event.clientY >= rect.bottom - revealDepth;
      const nearTop = event.clientY <= rect.top + revealDepth;
      if (nearBottom || !composerFocused) updateBottomActive(nearBottom);
      if (focused)
        updateFocusHover(event.currentTarget, event.clientX, event.clientY);
      else updateTopActive(nearTop);
    },
    [focused, rootRef, updateBottomActive, updateFocusHover, updateTopActive],
  );

  const handlePointerLeave = useCallback(() => {
    // Leaving the frame re-arms both edges: the next entry may show
    // chrome again. Snoozed edges never re-show while inside.
    topSnoozedRef.current = false;
    bottomSnoozedRef.current = false;
    const composer = rootRef.current?.querySelector(
      '[data-frame-hover-composer="true"]',
    );
    if (composer?.contains(document.activeElement)) return;
    rightZoneHoverRef.current = false;
    updateBottomActive(false);
    updateTopActive(false);
  }, [rootRef, updateBottomActive, updateTopActive]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const followUpperBand = (event: PointerEvent) => {
      if (focused) {
        updateFocusHover(root, event.clientX, event.clientY);
        return;
      }
      const rect = root.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top - 40 &&
        event.clientY <= rect.top
      ) {
        updateTopActive(true);
      } else if (!root.contains(event.target as Node)) {
        updateTopActive(false);
      }
    };
    const focusIn = (event: FocusEvent) => {
      if (
        topInteracting(root) ||
        (event.target instanceof Element &&
          event.target.matches(
            '[data-frame-chrome-drag-strip="true"]:focus-visible',
          ))
      )
        updateTopActive(true);
    };
    const focusOut = () => updateTopActive(false);
    document.addEventListener("pointermove", followUpperBand, {
      passive: true,
    });
    root.addEventListener("focusin", focusIn);
    root.addEventListener("focusout", focusOut);
    return () => {
      document.removeEventListener("pointermove", followUpperBand);
      root.removeEventListener("focusin", focusIn);
      root.removeEventListener("focusout", focusOut);
    };
  }, [focused, rootRef, updateFocusHover, updateTopActive]);

  useEffect(() => {
    clearTimer(topTimerRef);
    topActiveRef.current = false;
    setTopActive(false);
    if (focused) return;
    entryRevealShownRef.current = false;
    hoverArmedRef.current = false;
    rightZoneHoverRef.current = false;
    setEntryRevealActive(false);
    clearTimer(entryRevealTimerRef);
  }, [clearTimer, focused]);

  useEffect(() => {
    if (focused) beginEntryReveal();
  }, [beginEntryReveal, focused]);

  useEffect(() => {
    if (!maximized) return;
    clearTimer(topTimerRef);
    topActiveRef.current = false;
    setTopActive(false);
  }, [clearTimer, maximized]);

  useEffect(
    () => () => {
      clearTimer(topTimerRef);
      clearTimer(entryRevealTimerRef);
      clearTimer(bottomTimerRef);
      // A remount (StrictMode, Focus re-layout) must be able to reveal again.
      entryRevealShownRef.current = false;
    },
    [clearTimer],
  );

  return {
    bottomActive,
    topActive: topActive || entryRevealActive,
    handlePointerLeave,
    handlePointerMove,
    updateBottomActive,
    updateTopActive,
    snoozeTop,
    snoozeBottom,
  };
}
